import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { ADMISSION_STATUS_LABELS, isAwaitingBed } from '../../constants/admission';
import { formatDateIST } from '../../utils/formatIST';
import { isSearchableQuery, SEARCH_STATUS, SEARCH_ERROR } from './useAdmissionServerSearch';

/**
 * SearchSuggestInput (C6a) — debounced search box + suggestion dropdown for
 * Manage Admission. Purely presentational: it holds only its own text + open/
 * keyboard state and calls back out. It NEVER touches the network — matching is
 * done by the parent (in-memory rows + the debounced server search hook), so
 * typing fast cannot produce requests (the A2 storm class).
 *
 *  - value/onChange    controlled query (onChange fires DEBOUNCED, ~280ms)
 *  - onFocus()         fired on EVERY focus (parent re-warms its index; a cache hit is free)
 *  - suggestions       { items: admission[], more: number } from the parent
 *  - onPick(admission) a suggestion was chosen
 *  - busy              index still warming (shows "Searching…")
 *  - server            { status, errorCode, truncated, retry } from the org-wide
 *                      server search (useAdmissionServerSearch); absent = idle
 */
const DEBOUNCE_MS = 280;

const SERVER_IDLE = { status: SEARCH_STATUS.IDLE };

// Server rows carry mobile_last4; loaded rail/index rows carry the full number.
function mobileLast4(a) {
  if (a.mobile_last4) return a.mobile_last4;
  const digits = String(a.patient_mobile || '').replace(/\D/g, '');
  return digits ? digits.slice(-4) : null;
}

function bedText(a) {
  if (a.bed_label) return a.bed_label;
  if (isAwaitingBed(a)) return 'Awaiting bed';
  return null;
}

const SearchSuggestInput = forwardRef(function SearchSuggestInput(
  { value, onChange, suggestions, onFocus: onFocusProp, onPick, busy, server = SERVER_IDLE },
  ref,
) {
  const [text, setText] = useState(value || '');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  // bk-1790: the debounce timer must call the LATEST onChange — a stale closure would
  // re-spread pre-keystroke filters over any chip/view change made inside the window.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // S2 AC8 / S5 AC4 — "Not here? Search all patients" imperatively focuses
  // THIS box (one shared component, not a second search field).
  useImperativeHandle(ref, () => ({
    focus: () => { if (inputRef.current) inputRef.current.focus(); },
  }), []);

  // Keep local text in sync when the parent sets the query (e.g. suggestion pick).
  useEffect(() => { setText(value || ''); }, [value]);
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const items = (suggestions && suggestions.items) || [];
  const more = (suggestions && suggestions.more) || 0;
  const trimmed = text.trim();

  const emit = (next) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onChangeRef.current(next), DEBOUNCE_MS);
  };

  const onInput = (e) => {
    const next = e.target.value;
    setText(next);
    setActive(-1);
    setOpen(true);
    emit(next);
  };

  const onFocus = () => {
    if (onFocusProp) onFocusProp();
    if (trimmed) setOpen(true);
  };

  const pick = (a) => {
    setOpen(false);
    setActive(-1);
    // bk-1785: the pick replaces the typed query — a pending debounce would re-apply it.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (onPick) onPick(a);
  };

  const clear = () => {
    setText('');
    setOpen(false);
    setActive(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onChange('');
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      if (active >= 0 && active < items.length) {
        e.preventDefault();
        pick(items[active]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActive(-1);
    }
  };

  const showDropdown = open && trimmed.length > 0;
  const tooShort = !isSearchableQuery(trimmed);
  const loading = !tooShort && (server.status === SEARCH_STATUS.LOADING || !!busy);
  const failed = !tooShort && server.status === SEARCH_STATUS.ERROR;
  const emptyMatches = showDropdown && items.length === 0 && !tooShort && !loading && !failed && !server.truncated;

  return (
    <div className="relative ml-auto min-w-[240px] flex-[0_1_320px]" data-testid="search-suggest">
      <input
        ref={inputRef}
        type="text"
        className="w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        placeholder="Search any patient — name or IP no."
        value={text}
        onChange={onInput}
        onFocus={onFocus}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        aria-label="Search admissions"
        autoComplete="off"
      />

      {showDropdown && (
        <div
          className="absolute top-full left-0 right-0 mt-1 z-50 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl overflow-hidden"
          data-testid="suggest-box"
          role="listbox"
        >
          {items.map((a, i) => {
            const bt = bedText(a);
            return (
              <button
                type="button"
                key={a.admission_id}
                data-testid={`suggest-item-${a.admission_id}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => { e.preventDefault(); pick(a); }}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-700 last:border-b-0 ${
                  i === active ? 'bg-teal-50 dark:bg-teal-900/30' : 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
              >
                <span className="min-w-0">
                  <span className="block font-semibold text-slate-800 dark:text-slate-100 text-[13px] truncate">{a.patient_name || a.patient_id}</span>
                  <span className="block text-[11.5px] text-slate-500 dark:text-slate-400">
                    {[a.ip_number, a.uhid, mobileLast4(a) ? `••••${mobileLast4(a)}` : null].filter(Boolean).join(' · ')}
                  </span>
                  <span className="block text-[11px] text-slate-400 dark:text-slate-500">
                    {`Admitted ${formatDateIST(a.admitted_at || a.created_at) || '—'} · Discharged ${formatDateIST(a.discharged_at) || '—'}`}
                  </span>
                </span>
                <span className="ml-auto flex items-center gap-1.5 shrink-0">
                  <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full border border-amber-400 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">{bt || 'No bed'}</span>
                  <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300">{ADMISSION_STATUS_LABELS[a.status] || a.status}</span>
                </span>
              </button>
            );
          })}

          {server.truncated && !tooShort ? (
            <div className="px-3 py-2 text-[12px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900" data-testid="suggest-more">
              Many more matches — refine your search
            </div>
          ) : more > 0 && (
            <div className="px-3 py-2 text-[12px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900" data-testid="suggest-more">
              +{more} more — type more of the name or IP no.
            </div>
          )}
          {items.length > 0 && !loading && !failed && !tooShort && (
            <div className="px-3 py-1.5 text-[11px] text-slate-400 dark:text-slate-500 border-t border-slate-100 dark:border-slate-700">
              Searches all admissions, including discharged and cancelled
            </div>
          )}
          {tooShort && items.length === 0 && (
            <div className="px-3 py-3 text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="suggest-short">Type at least 2 letters or digits</div>
          )}
          {loading && (
            <div className="px-3 py-3 text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="suggest-loading">Searching…</div>
          )}
          {failed && (
            <div className="px-3 py-3 text-[13px] text-slate-600 dark:text-slate-300" data-testid="suggest-error">
              {server.errorCode === SEARCH_ERROR.RATE_LIMITED
                ? 'Too many searches right now. Try again in a minute.'
                : "Search didn't work. Check your connection and try again."}
              {server.errorCode !== SEARCH_ERROR.RATE_LIMITED && (
                <button type="button" onMouseDown={(e) => { e.preventDefault(); if (server.retry) server.retry(); }} className="ml-2 text-teal-700 dark:text-teal-300 font-medium underline">Try again</button>
              )}
            </div>
          )}
          {emptyMatches && (
            <div className="px-3 py-3 text-[13px] text-slate-600 dark:text-slate-300" data-testid="suggest-empty">
              No admissions match “{trimmed}”
              <button type="button" onMouseDown={(e) => { e.preventDefault(); clear(); }} className="ml-2 text-teal-700 dark:text-teal-300 font-medium underline">Clear</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default SearchSuggestInput;
